/**
 * Per-station, in-memory CSMS-initiated session state.
 *
 * Tech-debt #7 (Phase B) — these five structures used to be scattered
 * module-level `Map` globals inside `csmsCallHandler.ts` (reservations,
 * monitors, networkProfiles, certificates, deferredAvailability) plus a
 * loose `nextMonitorId` counter. That made the handler hold mutable global
 * state with no single owner: tests had to import each map and `.clear()` it
 * individually, and the reset contract lived in a separate function. They are
 * now owned by ONE object behind a typed API, with a single `clearStation()`
 * (soft/full reset) and `resetAll()` (tests) entry point.
 *
 * All of this is transient: it is NOT persisted to Dexie. A ReserveNow drives
 * a connector to `Reserved`, a SetVariableMonitoring installs a monitor, etc.
 * — none of which survives a reload, exactly like the connector status it
 * mirrors. Keeping it here (rather than a Zustand store) keeps it synchronous
 * and free of React-render coupling; a future UI surface can wrap it.
 */
import type { StationId } from '@/domain';

export interface ReservationRecord {
  readonly reservationId: number;
  readonly connectorId: number;
  readonly idTag: string;
  readonly expiryDate: string;
}

export interface MonitorRecord {
  readonly id: number;
  readonly component: string;
  readonly variable: string;
  readonly type: string;
  readonly value: number;
  readonly severity: number;
  readonly transaction: boolean;
}

export interface CertificateRecord {
  readonly certificateType: string;
  readonly hashData: {
    hashAlgorithm: string;
    issuerNameHash: string;
    issuerKeyHash: string;
    serialNumber: string;
  };
}

/** A ChangeAvailability the CSMS asked for while a tx was active (applied on stop). */
export interface DeferredAvailabilityRecord {
  readonly type: 'Operative' | 'Inoperative';
  readonly connectorId: number;
}

type TimerHandle = ReturnType<typeof setTimeout>;

class CsmsSessionState {
  // ── deferred availability ──────────────────────────────────────────────
  readonly #deferred = new Map<StationId, DeferredAvailabilityRecord>();

  setDeferredAvailability(stationId: StationId, rec: DeferredAvailabilityRecord): void {
    this.#deferred.set(stationId, rec);
  }
  getDeferredAvailability(stationId: StationId): DeferredAvailabilityRecord | undefined {
    return this.#deferred.get(stationId);
  }
  clearDeferredAvailability(stationId: StationId): void {
    this.#deferred.delete(stationId);
  }

  // ── reservations ───────────────────────────────────────────────────────
  readonly #reservations = new Map<StationId, Map<number, ReservationRecord>>();
  readonly #reservationTimers = new Map<StationId, Map<number, TimerHandle>>();

  getReservation(stationId: StationId, reservationId: number): ReservationRecord | undefined {
    return this.#reservations.get(stationId)?.get(reservationId);
  }
  listReservations(stationId: StationId): readonly ReservationRecord[] {
    const map = this.#reservations.get(stationId);
    return map ? [...map.values()] : [];
  }
  addReservation(stationId: StationId, rec: ReservationRecord): void {
    const map = this.#reservations.get(stationId) ?? new Map<number, ReservationRecord>();
    map.set(rec.reservationId, rec);
    this.#reservations.set(stationId, map);
  }
  /** Remove a reservation (and cancel its expiry timer); returns the removed record. */
  removeReservation(stationId: StationId, reservationId: number): ReservationRecord | undefined {
    const rec = this.#reservations.get(stationId)?.get(reservationId);
    this.#reservations.get(stationId)?.delete(reservationId);
    this.#cancelReservationTimer(stationId, reservationId);
    return rec;
  }
  /** Find the reservation held against a connector, if any (used on tx start). */
  findReservationByConnector(
    stationId: StationId,
    connectorId: number,
  ): ReservationRecord | undefined {
    return this.listReservations(stationId).find((r) => r.connectorId === connectorId);
  }
  setReservationTimer(stationId: StationId, reservationId: number, handle: TimerHandle): void {
    const map = this.#reservationTimers.get(stationId) ?? new Map<number, TimerHandle>();
    map.set(reservationId, handle);
    this.#reservationTimers.set(stationId, map);
  }
  #cancelReservationTimer(stationId: StationId, reservationId: number): void {
    const map = this.#reservationTimers.get(stationId);
    const handle = map?.get(reservationId);
    if (handle !== undefined) {
      clearTimeout(handle);
      map?.delete(reservationId);
    }
  }
  #cancelAllReservationTimers(stationId: StationId): void {
    const map = this.#reservationTimers.get(stationId);
    if (!map) return;
    for (const handle of map.values()) clearTimeout(handle);
    this.#reservationTimers.delete(stationId);
  }

  // ── variable monitors ──────────────────────────────────────────────────
  readonly #monitors = new Map<StationId, Map<number, MonitorRecord>>();
  // Periodic monitors carry a recurring timer (setInterval handle), cleared
  // when the monitor is removed or the station is reset.
  readonly #monitorTimers = new Map<StationId, Map<number, TimerHandle>>();
  #nextMonitorId = 1;

  /** Install a monitor; assigns an id when the request didn't carry one. Returns the id. */
  addMonitor(stationId: StationId, rec: Omit<MonitorRecord, 'id'> & { id?: number }): number {
    const id = rec.id ?? this.#nextMonitorId++;
    const map = this.#monitors.get(stationId) ?? new Map<number, MonitorRecord>();
    map.set(id, { ...rec, id });
    this.#monitors.set(stationId, map);
    return id;
  }
  hasMonitor(stationId: StationId, monitorId: number): boolean {
    return this.#monitors.get(stationId)?.has(monitorId) ?? false;
  }
  removeMonitor(stationId: StationId, monitorId: number): boolean {
    this.#cancelMonitorTimer(stationId, monitorId);
    return this.#monitors.get(stationId)?.delete(monitorId) ?? false;
  }
  listMonitors(stationId: StationId): readonly MonitorRecord[] {
    const map = this.#monitors.get(stationId);
    return map ? [...map.values()] : [];
  }
  /** Register a recurring timer for a (periodic) monitor; replaces any prior one. */
  setMonitorTimer(stationId: StationId, monitorId: number, handle: TimerHandle): void {
    this.#cancelMonitorTimer(stationId, monitorId);
    const map = this.#monitorTimers.get(stationId) ?? new Map<number, TimerHandle>();
    map.set(monitorId, handle);
    this.#monitorTimers.set(stationId, map);
  }
  #cancelMonitorTimer(stationId: StationId, monitorId: number): void {
    const map = this.#monitorTimers.get(stationId);
    const handle = map?.get(monitorId);
    if (handle !== undefined) {
      clearInterval(handle);
      map?.delete(monitorId);
    }
  }
  #cancelAllMonitorTimers(stationId: StationId): void {
    const map = this.#monitorTimers.get(stationId);
    if (!map) return;
    for (const handle of map.values()) clearInterval(handle);
    this.#monitorTimers.delete(stationId);
  }

  // ── network profiles ───────────────────────────────────────────────────
  readonly #networkProfiles = new Map<StationId, Map<number, Record<string, unknown>>>();

  setNetworkProfile(
    stationId: StationId,
    configurationSlot: number,
    connectionData: Record<string, unknown>,
  ): void {
    const slots =
      this.#networkProfiles.get(stationId) ?? new Map<number, Record<string, unknown>>();
    slots.set(configurationSlot, connectionData);
    this.#networkProfiles.set(stationId, slots);
  }
  getNetworkProfile(
    stationId: StationId,
    configurationSlot: number,
  ): Record<string, unknown> | undefined {
    return this.#networkProfiles.get(stationId)?.get(configurationSlot);
  }
  listNetworkProfiles(stationId: StationId): readonly Record<string, unknown>[] {
    const map = this.#networkProfiles.get(stationId);
    return map ? [...map.values()] : [];
  }

  // ── installed certificates ─────────────────────────────────────────────
  readonly #certificates = new Map<StationId, CertificateRecord[]>();

  addCertificate(stationId: StationId, rec: CertificateRecord): void {
    const list = this.#certificates.get(stationId) ?? [];
    list.push(rec);
    this.#certificates.set(stationId, list);
  }
  listCertificates(stationId: StationId): readonly CertificateRecord[] {
    return this.#certificates.get(stationId) ?? [];
  }
  /** Remove the first certificate matching `predicate`; returns whether one was removed. */
  removeCertificate(
    stationId: StationId,
    predicate: (rec: CertificateRecord) => boolean,
  ): boolean {
    const list = this.#certificates.get(stationId);
    if (!list) return false;
    const idx = list.findIndex(predicate);
    if (idx < 0) return false;
    list.splice(idx, 1);
    return true;
  }

  // ── lifecycle ──────────────────────────────────────────────────────────

  /**
   * Drop every in-memory structure for one station — called from
   * useStationStore's soft/full reset so this transient state follows the
   * same cleanup contract as the Dexie-backed per-station tables. Without it
   * the maps would leak (unbounded) across create/delete cycles.
   */
  clearStation(stationId: StationId): void {
    this.#cancelAllReservationTimers(stationId);
    this.#cancelAllMonitorTimers(stationId);
    this.#reservations.delete(stationId);
    this.#monitors.delete(stationId);
    this.#networkProfiles.delete(stationId);
    this.#certificates.delete(stationId);
    this.#deferred.delete(stationId);
  }

  /** Wipe ALL stations — for test isolation (replaces the old per-map `.clear()`). */
  resetAll(): void {
    for (const stationId of [...this.#reservationTimers.keys()]) {
      this.#cancelAllReservationTimers(stationId);
    }
    for (const stationId of [...this.#monitorTimers.keys()]) {
      this.#cancelAllMonitorTimers(stationId);
    }
    this.#reservations.clear();
    this.#reservationTimers.clear();
    this.#monitors.clear();
    this.#monitorTimers.clear();
    this.#networkProfiles.clear();
    this.#certificates.clear();
    this.#deferred.clear();
    this.#nextMonitorId = 1;
  }
}

/** Singleton owner of all transient CSMS-initiated per-station session state. */
export const csmsSession = new CsmsSessionState();
